class CollectionShowcase extends HTMLElement {
  constructor() {
    super();
    this.imageContainer = this.querySelector('.collection-showcase__image-container');
    this.collectionItems = this.querySelectorAll('.collection-showcase__item');
    this.images = this.querySelectorAll('.collection-showcase__image');
    this.currentActiveIndex = 0;
    
    this.init();
  }

  init() {
    if (!this.imageContainer || !this.collectionItems.length || !this.images.length) {
      return;
    }

    // Set up hover events
    this.setupHoverEvents();
    
    // Set up keyboard navigation
    this.setupKeyboardNavigation();
    
    // Set up intersection observer for preloading
    this.setupIntersectionObserver();
  }

  setupHoverEvents() {
    this.collectionItems.forEach((item, index) => {
      const link = item.querySelector('.collection-showcase__link');
      
      if (link) {
        // Mouse enter - show corresponding image
        link.addEventListener('mouseenter', () => {
          this.showImage(index);
          this.setActiveItem(index);
        });

        // Mouse leave - no longer reset to first image
        // The active state will persist until another item is hovered

        // Handle touch devices
        link.addEventListener('touchstart', (e) => {
          e.preventDefault();
          this.showImage(index);
          this.setActiveItem(index);
        });

        // Handle clicks - ensure image is shown before navigation
        link.addEventListener('click', (e) => {
          this.showImage(index);
          this.setActiveItem(index);
          // Let the default link behavior happen
        });
      }
    });

    // Container mouseleave - keep current active state
    // No reset behavior needed
  }

  setupKeyboardNavigation() {
    this.collectionItems.forEach((item, index) => {
      const link = item.querySelector('.collection-showcase__link');
      
      if (link) {
        link.addEventListener('focus', () => {
          this.showImage(index);
          this.setActiveItem(index);
        });

        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const direction = e.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = this.getNextIndex(index, direction);
            const nextLink = this.collectionItems[nextIndex]?.querySelector('.collection-showcase__link');
            
            if (nextLink) {
              nextLink.focus();
            }
          }
        });
      }
    });
  }

  setupIntersectionObserver() {
    // Preload images when section comes into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.preloadImages();
          observer.unobserve(this);
        }
      });
    }, { 
      threshold: 0.1,
      rootMargin: '50px'
    });

    observer.observe(this);
  }

  showImage(index) {
    if (index === this.currentActiveIndex) return;

    // Hide current active image
    const currentImage = this.images[this.currentActiveIndex];
    if (currentImage) {
      currentImage.classList.remove('active');
    }

    // Show new image
    const newImage = this.images[index];
    if (newImage) {
      newImage.classList.add('active');
      this.currentActiveIndex = index;

      // Dispatch custom event for analytics or other integrations
      this.dispatchEvent(new CustomEvent('collection-showcase:image-changed', {
        detail: { 
          index, 
          blockId: newImage.getAttribute('data-block-id'),
          element: newImage 
        },
        bubbles: true
      }));
    }
  }

  setActiveItem(index) {
    // Remove active class from all items
    this.collectionItems.forEach((item) => {
      item.classList.remove('active');
    });

    // Add active class to current item
    const currentItem = this.collectionItems[index];
    if (currentItem) {
      currentItem.classList.add('active');
    }
  }

  // isHoveringOverItems method removed - no longer needed
  // Active state persists until another item is hovered

  getNextIndex(currentIndex, direction) {
    const maxIndex = this.collectionItems.length - 1;
    let nextIndex = currentIndex + direction;

    if (nextIndex > maxIndex) {
      nextIndex = 0;
    } else if (nextIndex < 0) {
      nextIndex = maxIndex;
    }

    return nextIndex;
  }

  preloadImages() {
    // Preload non-visible images for smoother transitions
    this.images.forEach((imageContainer, index) => {
      if (index !== 0) { // Skip the first image as it's already loaded
        const img = imageContainer.querySelector('img');
        if (img && img.loading === 'lazy') {
          // Force loading of lazy images
          img.loading = 'eager';
        }
      }
    });
  }

  // Public method to programmatically change image
  changeImage(index) {
    if (index >= 0 && index < this.images.length) {
      this.showImage(index);
      this.setActiveItem(index);
    }
  }

  // Public method to get current active index
  getActiveIndex() {
    return this.currentActiveIndex;
  }
}

// Register the custom element
customElements.define('collection-showcase', CollectionShowcase);

// Fallback for browsers that don't support custom elements
if (!customElements.get('collection-showcase')) {
  // Simple fallback implementation
  document.addEventListener('DOMContentLoaded', () => {
    const showcases = document.querySelectorAll('.collection-showcase__wrapper');
    
    showcases.forEach(showcase => {
      const items = showcase.querySelectorAll('.collection-showcase__item');
      const images = showcase.querySelectorAll('.collection-showcase__image');
      
      items.forEach((item, index) => {
        const link = item.querySelector('.collection-showcase__link');
        
        if (link) {
          link.addEventListener('mouseenter', () => {
            // Remove active from all items and images
            images.forEach(img => img.classList.remove('active'));
            items.forEach(itm => itm.classList.remove('active'));
            
            // Set current as active (will persist until another is hovered)
            if (images[index]) images[index].classList.add('active');
            item.classList.add('active');
          });
          
          // No mouseleave handler - keep active state
        }
      });
    });
  });
}

// Export for potential module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionShowcase;
}